import { FunctionsHttpError } from "@supabase/supabase-js";
import { AppError, requireData, toAppError } from "../../lib/appError";
import type { Database } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";

export type Domain = Database["public"]["Tables"]["domains"]["Row"];

// What anon may read of a domain (column-level grant, see the
// lock_domains_writes_and_columns migration). `select("*")` fails for roles
// with column grants, so public lookups name these columns explicitly.
export type PublicDomain = Pick<
  Domain,
  "id" | "site_id" | "hostname" | "is_primary" | "status"
>;
const PUBLIC_DOMAIN_COLUMNS = "id, site_id, hostname, is_primary, status";

// What a signed-in owner may read of a domain: everything except
// provider_hostname_id and verification_token_hash (column-level grant), with
// `edge_registered` standing in for "has a Cloudflare hostname id". The
// edge-function and RPC responses are full rows, a superset of this shape.
export type OwnerDomain = Omit<
  Domain,
  "provider_hostname_id" | "verification_token_hash"
> & { edge_registered: boolean };
const OWNER_DOMAIN_COLUMNS =
  "id, site_id, hostname, is_primary, status, verified_at, last_checked_at, created_at, updated_at, expected_cname, failure_reason, edge_registered";

type CloudflareStatus = {
  status: string;
  ssl_status: string | null;
  ssl_error?: string | null;
};

export type AddDomainResult = {
  domain: OwnerDomain;
  dns: { type: "CNAME"; name: string; target: string };
  is_apex: boolean;
  already_exists?: boolean;
  cloudflare?: CloudflareStatus;
  ownership_verification?: Record<string, unknown> | null;
};

export type VerifyDomainResult = {
  domain: OwnerDomain;
  cloudflare?: CloudflareStatus;
};

// The function answers errors as { error: "…" }; FunctionsHttpError keeps the
// raw Response on .context so the UI-facing message can be recovered.
const invokeManageDomain = async <T>(
  body: Record<string, unknown>,
  fallback: string,
): Promise<T> => {
  const { data, error } = await supabase.functions.invoke<T>("manage-domain", {
    body,
  });
  if (error) {
    let message = fallback;
    if (error instanceof FunctionsHttpError) {
      const context = (await error.context.json().catch(() => null)) as {
        error?: string;
      } | null;
      if (context?.error) message = context.error;
    }
    throw new AppError(message, "MANAGE_DOMAIN_FAILED", error);
  }
  return data as T;
};

export const domainRepository = {
  async findActiveByHostname(hostname: string): Promise<PublicDomain | null> {
    const { data, error } = await supabase
      .from("domains")
      .select(PUBLIC_DOMAIN_COLUMNS)
      .eq("hostname", hostname)
      .eq("status", "active")
      .maybeSingle();
    if (error) throw toAppError(error, "Could not resolve the hostname.");
    return data;
  },

  async listForSite(siteId: string): Promise<OwnerDomain[]> {
    const { data, error } = await supabase
      .from("domains")
      .select(OWNER_DOMAIN_COLUMNS)
      .eq("site_id", siteId)
      .order("created_at", { ascending: true });
    // edge_registered is missing from the generated types until the migration
    // is applied and the types regenerated, so the select result is untyped.
    return requireData(
      data as unknown as OwnerDomain[] | null,
      error,
      "Could not load the domains.",
    );
  },

  async addDomain(siteId: string, hostname: string): Promise<AddDomainResult> {
    return invokeManageDomain<AddDomainResult>(
      { action: "add", site_id: siteId, hostname },
      "Could not add the domain.",
    );
  },

  async verifyDomain(domainId: string): Promise<VerifyDomainResult> {
    return invokeManageDomain<VerifyDomainResult>(
      { action: "verify", domain_id: domainId },
      "Could not verify the domain.",
    );
  },

  async removeDomain(domainId: string): Promise<void> {
    await invokeManageDomain<{ deleted: boolean }>(
      { action: "remove", domain_id: domainId },
      "Could not remove the domain.",
    );
  },

  // The site's canonical hostname: its primary active custom domain, or null
  // when the site has no primary (the *.<root> subdomain is then canonical).
  async findPrimaryHostname(siteId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("domains")
      .select("hostname")
      .eq("site_id", siteId)
      .eq("is_primary", true)
      .eq("status", "active")
      .maybeSingle();
    if (error) throw toAppError(error, "Could not resolve the hostname.");
    return data?.hostname ?? null;
  },

  async setPrimary(domainId: string): Promise<OwnerDomain> {
    const { data, error } = await supabase.rpc("set_domain_primary", {
      p_domain_id: domainId,
    });
    // The RPC returns the full row (security definer), a superset of
    // OwnerDomain; the generated type lacks edge_registered until regenerated.
    return requireData(
      data as unknown as OwnerDomain | null,
      error,
      "Could not set the primary domain.",
    );
  },
};
